import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Shield, Users, DollarSign, Trophy } from 'lucide-react';

const HomePage: React.FC = () => {
  const stats = [
    { label: 'Treasury Value', value: '7.2M SUI', icon: DollarSign },
    { label: 'Current APY', value: '15.7%', icon: TrendingUp },
    { label: 'Champion Horses', value: '12', icon: Trophy },
    { label: 'Active Members', value: '247', icon: Users },
  ];

  const features = [
    {
      icon: '💰',
      title: 'Dual Income Streams',
      description: 'Earn from both racing purses and SUI staking rewards. Current members see 15.7% APY.',
    },
    {
      icon: '🏇',
      title: 'Real Horse Ownership',
      description: 'Own actual champion thoroughbreds as NFTs with complete provenance and performance data.',
    },
    {
      icon: '🗳️',
      title: 'Community Governed',
      description: 'Vote on horse acquisitions, breeding programs, and treasury management through DAO.',
    },
    {
      icon: '🔒',
      title: 'Institutional Security',
      description: 'Multi-sig treasury, insurance coverage, and legal compliance exceed traditional funds.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              The Future of{' '}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Thoroughbred Racing
              </span>
            </h1>
            <p className="text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
              Join the first community-owned, yield-backed racing syndicate on Sui blockchain. 
              Own champion horses, earn staking rewards, and race toward victory.
            </p>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
              {stats.map((stat, index) => (
                <div key={index} className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center justify-center mb-2">
                    <stat.icon className="w-8 h-8 text-cyan-400" />
                  </div>
                  <div className="text-2xl font-bold text-white">{stat.value}</div>
                  <div className="text-slate-400 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/auth"
                className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-8 py-4 rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <span>Join Syndicate</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/about"
                className="border border-cyan-500 text-cyan-400 px-8 py-4 rounded-lg hover:bg-cyan-500/10 transition-all duration-200"
              >
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-800/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Why SUI-ndicate?
            </h2>
            <p className="text-xl text-slate-300">
              Revolutionary blockchain-based horse racing syndicate with dual income streams
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6 hover:border-cyan-500/50 transition-all duration-200">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-slate-300">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Returns Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Projected Returns
            </h2>
            <p className="text-xl text-slate-300">
              15-year investment horizon with compounding dual streams
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-white mb-2">Years 1-4</h3>
              <div className="text-3xl font-bold text-green-400 mb-2">10-15% IRR</div>
              <p className="text-slate-300">70% Staking, 30% Racing</p>
            </div>
            <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-white mb-2">Years 5-10</h3>
              <div className="text-3xl font-bold text-blue-400 mb-2">12-18% IRR</div>
              <p className="text-slate-300">55% Staking, 45% Racing & Breeding</p>
            </div>
            <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-white mb-2">Years 11-15</h3>
              <div className="text-3xl font-bold text-purple-400 mb-2">15-20% IRR</div>
              <p className="text-slate-300">40% Staking, 60% Horse Sales & Stud Fees</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-cyan-500/10 to-blue-500/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Join the Syndicate?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Start with as little as 1,000 SUI and become part of racing history
          </p>
          <Link
            to="/auth"
            className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-8 py-4 rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-200 inline-flex items-center space-x-2"
          >
            <span>Get Started Now</span>
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
